import { FC } from "react";
import { z } from "zod";

import { SelectItem } from "@roqua/components/ui/select";

import { SelectWithLabel } from "@/components/ui/select-with-label";
import { RespondentTypeEnum } from "@/generated/gql/graphql";
import { useTranslation } from "@/i18n/client";
import { zodRespondentTypeEnum } from "@/models/respondent";
import { useSessionContext } from "@/session";

export type Filters = {
  searchTerm?: string;
  type?: TypeFilterValue;
  respondentType?: RespondentTypeFilterValue;
  teamId?: string;
};

export const TeamFilter: FC<{ onValueChange: (value: string) => void }> = ({ onValueChange }) => {
  const { organization, user } = useSessionContext();

  const { t } = useTranslation(["Measure", "common"]);

  const handleChange = (value: string) => {
    onValueChange(value);
  };

  if (organization.teams.length === 0) {
    // If the organization doesn't use teams, we don't show the filter
    return null;
  }

  return (
    <SelectWithLabel
      label={t("teamFilter.label")}
      value={user.team?.id || "none"}
      onValueChange={handleChange}
      data-testid="teamfilter"
    >
      <SelectItem value="none">
        {organization.settings.requireTeamBeforeSelectingProtocol
          ? t("teamFilter.choose")
          : t("teamFilter.showAllProtocols")}
      </SelectItem>

      {organization.teams.map((team) => (
        <SelectItem key={team.id} value={team.id}>
          {team.name}
        </SelectItem>
      ))}
    </SelectWithLabel>
  );
};

export const typeFilterValue = z.enum([
  "all",
  "autoProtocols",
  "measurements",
  "modules",
  "favorites",
]);
export type TypeFilterValue = z.infer<typeof typeFilterValue>;

export const TypeFilter: FC<{
  value: TypeFilterValue;
  onValueChange: (value: TypeFilterValue) => void;
}> = ({ value, onValueChange }) => {
  const { t } = useTranslation(["Measure", "common"]);

  const handleChange = (value: string) => {
    const typedValue = typeFilterValue.parse(value);
    onValueChange(typedValue);
  };

  return (
    <SelectWithLabel
      label={t("typeFilter.label")}
      value={value}
      onValueChange={handleChange}
      data-testid="typefilter"
    >
      <SelectItem value="all">{t("typeFilter.all")}</SelectItem>
      <SelectItem value="autoProtocols">{t("typeFilter.autoProtocols")}</SelectItem>
      <SelectItem value="measurements">{t("typeFilter.measurements")}</SelectItem>
      <SelectItem value="modules">{t("typeFilter.modules")}</SelectItem>
    </SelectWithLabel>
  );
};

export const respondentTypeFilterValue = z.enum(["all", ...zodRespondentTypeEnum.options]);
export type RespondentTypeFilterValue = z.infer<typeof respondentTypeFilterValue>;

export const RespondentTypeFilter: FC<{
  value: RespondentTypeFilterValue;
  onValueChange: (value: RespondentTypeFilterValue) => void;
}> = ({ value, onValueChange }) => {
  const { t } = useTranslation(["Measure", "common"]);

  const handleChange = (value: string) => {
    const typedValue = respondentTypeFilterValue.parse(value);
    onValueChange(typedValue);
  };

  return (
    <SelectWithLabel
      label={t("respondentFilter.label")}
      value={value}
      onValueChange={handleChange}
      data-testid="respondentfilter"
    >
      <SelectItem value="all">{t("respondentFilter.all")}</SelectItem>
      <SelectItem value={RespondentTypeEnum.Patient}>
        {t("common:respondent.type.patient")}
      </SelectItem>
      <SelectItem value={RespondentTypeEnum.Parent}>
        {t("common:respondent.type.parent")}
      </SelectItem>
      <SelectItem value={RespondentTypeEnum.SecondParent}>
        {t("common:respondent.type.second_parent")}
      </SelectItem>
      <SelectItem value={RespondentTypeEnum.Caregiver}>
        {t("common:respondent.type.caregiver")}
      </SelectItem>
      <SelectItem value={RespondentTypeEnum.Teacher}>
        {t("common:respondent.type.teacher")}
      </SelectItem>
      <SelectItem value={RespondentTypeEnum.Profess}>
        {t("common:respondent.type.profess")}
      </SelectItem>
    </SelectWithLabel>
  );
};
